// Patient-portal pay-now flow for paying outstanding dues / blocked sessions.
//
// Flow:
//   1. POST /api/secure/me/payments/dues/{dueId}/pay
//      → backend creates the Razorpay order server-side via the existing
//        RazorpayOrderService.purchaseEvent infra, returns
//        { gateway: 'razorpay', payload: { orderId, amount, currency, razorpayClientId, ... } }
//   2. displayRazorpay(payload) opens the Razorpay checkout modal
//
// We deliberately do NOT route through /api/secure/razorpay/one-time-purchase/payload
// for pay-due — that endpoint is hard-wired to the SubscriptionPlan/SKU paywall
// flow and 7022s ("subscription not found") for paying off a booked session.

import { displayRazorpay } from 'src/consumer/services/paymentService'
import { patientPost } from './patientApi'

/**
 * Hit the patient-portal pay-now endpoint. Returns
 * { gateway, payload } where payload is ready for displayRazorpay.
 */
async function initPayment(dueId) {
  const res = await patientPost(
    `/api/secure/me/payments/dues/${encodeURIComponent(dueId)}/pay`,
    {},
    { requirePatientContext: true },
  )
  return res?.data || null
}

/**
 * End-to-end pay-now for a dueId. Resolves once the Razorpay modal is open
 * (or rejects on a setup error). The afterSuccess callback runs when the
 * patient completes payment.
 */
export async function payDueNow(dueId, { onSuccess } = {}) {
  if (!dueId) throw new Error('dueId required')
  const envelope = await initPayment(dueId)
  const gateway = (envelope?.gateway || 'razorpay').toLowerCase()
  if (gateway !== 'razorpay') {
    // Stripe / other gateways aren't wired into the patient portal yet.
    // Surface a clear error so the user can fall back to the consumer flow.
    throw new Error(`Gateway ${gateway} not supported in patient portal yet`)
  }
  const payload = envelope?.payload
  if (!payload?.orderId) {
    throw new Error('Could not create payment order')
  }
  await displayRazorpay(payload, null, () => {
    if (typeof onSuccess === 'function') {
      onSuccess()
    } else {
      window.location.reload()
    }
  })
}
